import { Alert, Button } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import { ConsentRequestButton } from './parts.js';
import { reasonKey } from './state.js';

/**
 * Back from Microsoft's sign-in page (MsBaglandi): `?microsoft365=connected` says it worked and what the person can
 * do now; `?microsoft365=error&reason=…` says in plain words why it did not (never the raw code), with "Yöneticiye
 * bildir" when Microsoft asked for the admin's consent. Closing it takes its words out of the address and keeps
 * the rest (and the page behind the settings dialog).
 */
export function Microsoft365Result() {
  const { t } = useTranslation();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  // a return address of its own may carry a value too (/c/<id>?microsoft365=resume): the result is the one that says so
  const values = params.getAll('microsoft365');
  const result = values.includes('error') ? 'error' : values.includes('connected') ? 'connected' : null;
  if (!result) return null;
  const reason = reasonKey(params.get('reason'));
  const close = () =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('microsoft365');
        next.delete('reason');
        return next;
      },
      { replace: true, state: location.state },
    );
  const closeButton = (
    <Button size="sm" variant="ghost" onClick={close} data-testid="microsoft365-result-close">
      {t('common.close')}
    </Button>
  );
  if (result === 'connected') {
    return <Alert tone="success" title={t('microsoft365.user.result.connected')} action={closeButton} data-testid="microsoft365-result" />;
  }
  return (
    <Alert
      tone="error"
      title={t('microsoft365.user.result.failed')}
      action={
        <div className="m365-result__actions">
          {reason === 'consent_required' ? <ConsentRequestButton /> : null}
          {closeButton}
        </div>
      }
      data-testid="microsoft365-result"
    >
      <p data-reason={reason}>{t(`microsoft365.user.reasons.${reason}`)}</p>
    </Alert>
  );
}
